import { createContext, useReducer, useCallback } from "react";

import axios from "../../utils/axios/v1/userAxios";

const SET_CONFIG = "SET_CONFIG";
const SET_LOADING = "SET_LOADING";
const SET_ERROR = "SET_ERROR";
const SET_CLIENT_IP = "SET_CLIENT_IP";

const initialState = {
  config: null,
  loading: false,
  errMsg: null,
  clientIp: null,
  clientIpAllowed: null,
};

const IPRestrictionReducer = (state, action) => {
  switch (action.type) {
    case SET_CONFIG:
      return {
        ...state,
        config: action.payload.config,
      };
    case SET_LOADING:
      return {
        ...state,
        loading: action.payload.loading,
      };
    case SET_ERROR:
      return {
        ...state,
        errMsg: action.payload.errMsg,
      };
    case SET_CLIENT_IP:
      return {
        ...state,
        clientIp: action.payload.clientIp,
        clientIpAllowed: action.payload.allowed,
      };
    default:
      return state;
  }
};

const IPRestrictionContext = createContext(null);

function IPRestrictionProvider({ children }) {
  const [state, dispatch] = useReducer(IPRestrictionReducer, initialState);

  const setConfig = useCallback((config) => {
    dispatch({
      type: SET_CONFIG,
      payload: { config },
    });
  }, []);

  const setLoading = useCallback((loading) => {
    dispatch({
      type: SET_LOADING,
      payload: { loading },
    });
  }, []);

  const setErr = useCallback((errMsg) => {
    dispatch({
      type: SET_ERROR,
      payload: { errMsg },
    });
  }, []);

  const setClientIp = useCallback((clientIp, allowed) => {
    dispatch({
      type: SET_CLIENT_IP,
      payload: { clientIp, allowed },
    });
  }, []);

  const getIPRestrictionConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get("/organisation/ip-restriction");
      setConfig(response.data);
      setErr(null);
      return response.data;
    } catch (err) {
      setConfig(null);
      setErr(err.message || "Failed to fetch IP restriction config");
      return null;
    } finally {
      setLoading(false);
    }
  }, [setConfig, setErr, setLoading]);

  const setIPRestrictionEnabled = useCallback(async (enabled) => {
    setLoading(true);
    try {
      const response = await axios.patch("/organisation/ip-restriction/enabled", { enabled });
      setConfig(response.data);
      setErr(null);
      return response.data;
    } catch (err) {
      setErr(err.message || "Failed to update IP restriction");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [setConfig, setErr, setLoading]);

  const addIP = useCallback(async (ip, description) => {
    setLoading(true);
    try {
      const response = await axios.post("/organisation/ip-restriction/ip", { ip, description });
      await getIPRestrictionConfig();
      return response.data;
    } catch (err) {
      setErr(err.message || "Failed to add IP");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getIPRestrictionConfig, setErr, setLoading]);

  const updateIPDescription = useCallback(async (ipId, description) => {
    setLoading(true);
    try {
      const response = await axios.patch(`/organisation/ip-restriction/ip/${ipId}`, { description });
      await getIPRestrictionConfig();
      return response.data;
    } catch (err) {
      setErr(err.message || "Failed to update IP description");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getIPRestrictionConfig, setErr, setLoading]);

  const removeIP = useCallback(async (ipId) => {
    setLoading(true);
    try {
      await axios.delete(`/organisation/ip-restriction/ip/${ipId}`);
      await getIPRestrictionConfig();
    } catch (err) {
      setErr(err.message || "Failed to remove IP");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getIPRestrictionConfig, setErr, setLoading]);

  const checkClientIP = useCallback(async () => {
    // First try to check if IP is allowed (when restrictions are enabled)
    try {
      const response = await axios.get("/organisation/ip-restriction/check");
      if (response.data.clientIp) {
        setClientIp(response.data.clientIp, response.data.allowed);
        return response.data;
      }
    } catch (err) {
      // Check endpoint failed, try my-ip endpoint
    }
    
    // Fallback: get IP from my-ip endpoint
    try {
      const response = await axios.get("/organisation/ip-restriction/my-ip");
      if (response.data.ip) {
        setClientIp(response.data.ip, null);
        return { clientIp: response.data.ip, allowed: null };
      }
    } catch (fallbackErr) {
      setErr("Failed to detect client IP");
    }
    return null;
  }, [setClientIp, setErr]);

  return (
    <IPRestrictionContext.Provider
      value={{
        ...state,
        getIPRestrictionConfig,
        setIPRestrictionEnabled,
        addIP,
        updateIPDescription,
        removeIP,
        checkClientIP,
        setErr,
      }}
    >
      {children}
    </IPRestrictionContext.Provider>
  );
}

export { IPRestrictionContext, IPRestrictionProvider };
